/**
 * AURA Widget Shell
 * Web Component wrapper with Shadow DOM isolation
 */

import { AuraConfig } from '../core/types';
import { Logger } from '../core/logger';
import { setLanguage } from '../core/i18n';
import { CSS_VARIABLES, RESET_STYLES } from './styles/variables';
import { getIcon } from './styles/icons';
import { createFab } from './fab';
import { createPanel } from './panel';
import {
  TOGGLE_STYLES,
  SLIDER_STYLES,
  COLOR_PICKER_STYLES,
  PROFILE_BUTTON_STYLES,
  ICON_BUTTON_STYLES,
  BUTTON_GROUP_STYLES,
} from './controls';
import { USEFUL_LINKS_STYLES } from '../modules/orientation/useful-links';
import { TOOLBAR_STYLES } from './toolbar';
import { SEARCH_BAR_STYLES } from './search-bar';
import { LANGUAGE_SELECTOR_STYLES } from './language-selector';
import { STATEMENT_MODAL_STYLES } from './statement-modal';
import { initProfilesModule, LINKED_PROFILE_STYLES } from '../modules/profiles';
import { initContentModule } from '../modules/content';
import { initColorModule } from '../modules/color';
import { initOrientationModule } from '../modules/orientation';
import { maybeMountDebugOverlay } from './debug-overlay';

const shellLogger = new Logger('Shell');

/**
 * Create the widget shell and inject into the document
 */
export function createWidgetShell(config: AuraConfig): HTMLElement {
  if (config.language) {
    setLanguage(config.language);
  }

  const shell = document.createElement('div');
  shell.setAttribute('data-aura', 'shell');
  // Touch capability detection (not media queries): host sites that set a
  // desktop viewport meta tag — or browsers in "request desktop site" mode —
  // defeat both (max-width: ...) and (pointer: coarse) on real phones.
  // navigator.maxTouchPoints reports the actual hardware and cannot be
  // overridden by viewport / UA tricks.  CSS keys off this attribute.
  // Belt-and-braces touch / mobile detection.  Each signal can be defeated
  // in some edge case, so we trust the answer the first one of them gives:
  //   - userAgentData.mobile       — Client Hints survive UA spoofing
  //   - navigator.maxTouchPoints   — fails in desktop-site-mode
  //   - 'ontouchstart' in window   — fails in desktop-site-mode
  //   - (pointer: coarse)          — fails when stylus reports as primary
  //   - (any-pointer: coarse)      — broader catch
  //   - (hover: none)              — phones / pads typically lack hover
  //   - innerWidth <= 1024         — generous "this is a small viewport"
  const uaData = (navigator as unknown as {
    userAgentData?: { mobile?: boolean };
  }).userAgentData;
  const mm = (q: string) =>
    typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
  const isTouchOrMobile =
    uaData?.mobile === true ||
    navigator.maxTouchPoints > 0 ||
    'ontouchstart' in window ||
    mm('(pointer: coarse)') ||
    mm('(any-pointer: coarse)') ||
    mm('(hover: none)') ||
    window.innerWidth <= 1024;
  shell.setAttribute('data-aura-input', isTouchOrMobile ? 'touch' : 'fine');

  const shadow = shell.attachShadow({ mode: 'closed' });

  const theme = detectTheme(config.theme);
  shell.setAttribute('data-theme', theme);

  const fab = createFab(config);
  const panel = createPanel(config);

  const allStyles = `
    ${CSS_VARIABLES}
    ${RESET_STYLES}
    ${fab.styles}
    ${panel.styles}
    ${TOGGLE_STYLES}
    ${SLIDER_STYLES}
    ${COLOR_PICKER_STYLES}
    ${PROFILE_BUTTON_STYLES}
    ${ICON_BUTTON_STYLES}
    ${USEFUL_LINKS_STYLES}
    ${BUTTON_GROUP_STYLES}
    ${LINKED_PROFILE_STYLES}
    ${TOOLBAR_STYLES}
    ${SEARCH_BAR_STYLES}
    ${LANGUAGE_SELECTOR_STYLES}
    ${STATEMENT_MODAL_STYLES}
  `;

  const styleElement = document.createElement('style');
  styleElement.textContent = allStyles;

  shadow.appendChild(styleElement);
  shadow.appendChild(panel.elements.overlay);
  shadow.appendChild(panel.elements.panel);
  shadow.appendChild(panel.elements.statementModal.overlay);
  shadow.appendChild(fab.element);

  // Theme toggle – only shown when the site has no detectable theme signals.
  // Re-evaluated each sync cycle so late-loading SPAs are handled.
  let manualOverride = false;
  let toggleBtn: HTMLButtonElement | null = null;

  const syncToggleIcon = () => {
    if (!toggleBtn) return;
    const isDark = shell.getAttribute('data-theme') === 'dark';
    toggleBtn.innerHTML = isDark ? getIcon('themeSun') : getIcon('themeMoon');
    toggleBtn.setAttribute('aria-label', isDark ? 'Switch to light mode' : 'Switch to dark mode');
  };

  const ensureToggle = () => {
    if (getPageThemeSignal() !== null) {
      if (toggleBtn) {
        toggleBtn.remove();
        toggleBtn = null;
        manualOverride = false;
      }
      return;
    }
    if (toggleBtn) return;

    toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.className = 'aura-panel__btn';
    toggleBtn.setAttribute('data-aura', 'theme-toggle');
    syncToggleIcon();

    toggleBtn.addEventListener('click', () => {
      const next = shell.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
      shell.setAttribute('data-theme', next);
      manualOverride = true;
      syncToggleIcon();
    });

    const headerRight = shadow.querySelector('.aura-panel__header-right');
    const closeBtn = headerRight?.querySelector('.aura-panel__btn');
    if (headerRight && closeBtn) {
      headerRight.insertBefore(toggleBtn, closeBtn);
    }
  };

  ensureToggle();

  try {
    watchTheme(shell, config.theme, () => manualOverride, () => {
      syncToggleIcon();
      ensureToggle();
    });
  } catch (e) {
    shellLogger.warn('Theme watcher failed to start', { error: String(e) });
  }

  initializePanelContent(panel.elements.tabPanels, config);

  const contentContainer = panel.elements.contentContainer;

  const moduleInits: [string, () => void][] = [
    ['profiles', () => initProfilesModule(contentContainer)],
    ['content', () => initContentModule(contentContainer)],
    ['color', () => initColorModule(contentContainer)],
    ['orientation', () => initOrientationModule(contentContainer)],
  ];
  for (const [name, initFn] of moduleInits) {
    try {
      initFn();
    } catch (e) {
      shellLogger.error(`Failed to initialize ${name} module`, e);
    }
  }

  if (config.zIndex) {
    shell.style.setProperty('--aura-z-fab', String(config.zIndex - 1));
    shell.style.setProperty('--aura-z-panel', String(config.zIndex));
  }

  // ?aura-debug=1 in the host URL shows a diagnostic overlay reporting
  // every signal that drives the panel-layout decision.  Closure captures
  // a reference to the panel so we can read getBoundingClientRect() on
  // it from outside the closed shadow root.
  try {
    maybeMountDebugOverlay(panel.elements.panel);
  } catch (e) {
    shellLogger.warn('Debug overlay failed to mount', { error: String(e) });
  }

  return shell;
}

/**
 * Detect the current theme by inspecting the host page.
 * Priority: explicit config > page signals > background luminance > default light.
 */
function detectTheme(configTheme?: 'light' | 'dark' | 'auto'): 'light' | 'dark' {
  if (configTheme === 'light' || configTheme === 'dark') return configTheme;

  const pageTheme = getPageThemeSignal();
  if (pageTheme) return pageTheme;

  const lum = getPageLuminance();
  if (lum !== null) return lum < 0.4 ? 'dark' : 'light';

  return 'light';
}

function getPageThemeSignal(): 'light' | 'dark' | null {
  const THEME_ATTRS = [
    'data-theme', 'data-color-scheme', 'data-bs-theme',
    'data-color-mode', 'data-mode', 'data-appearance',
  ];

  const targets: Element[] = [document.documentElement];
  if (document.body) targets.push(document.body);
  const appRoot = document.body?.firstElementChild;
  if (appRoot && !appRoot.hasAttribute('data-aura')) targets.push(appRoot);

  for (const el of targets) {
    if (!el) continue;

    for (const attr of THEME_ATTRS) {
      const val = el.getAttribute(attr)?.toLowerCase() ?? '';
      if (val.includes('dark')) return 'dark';
      if (val.includes('light')) return 'light';
    }

    // Tailwind uses a bare "dark" / "light" class on <html>
    if (el.classList?.contains('dark')) return 'dark';
    if (el.classList?.contains('light')) return 'light';

    const cls = (typeof el.className === 'string' ? el.className : '').toLowerCase();
    if (cls.includes('dark-mode') || cls.includes('dark-theme') || cls.includes('theme-dark')) return 'dark';
    if (cls.includes('light-mode') || cls.includes('light-theme') || cls.includes('theme-light')) return 'light';
  }

  // CSS color-scheme property on the root element
  try {
    const cs = getComputedStyle(document.documentElement).colorScheme;
    if (cs && cs !== 'normal' && cs !== 'auto') {
      if (cs.includes('dark') && !cs.includes('light')) return 'dark';
      if (cs.includes('light') && !cs.includes('dark')) return 'light';
    }
  } catch (_) { /* noop */ }

  return null;
}

function getPageLuminance(): number | null {
  const lum = (r: number, g: number, b: number): number => {
    const lin = (c: number) =>
      c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    return 0.2126 * lin(r / 255) + 0.7152 * lin(g / 255) + 0.0722 * lin(b / 255);
  };

  const parseBg = (el: Element): number | null => {
    const bg = getComputedStyle(el).backgroundColor;
    const m = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (!m) return null;
    if (m[4] !== undefined && parseFloat(m[4]) === 0) return null;
    return lum(+m[1]!, +m[2]!, +m[3]!);
  };

  try {
    const candidates: Element[] = [document.documentElement];
    if (document.body) candidates.unshift(document.body);
    const appRoot = document.body?.firstElementChild;
    if (appRoot && !appRoot.hasAttribute('data-aura')) candidates.unshift(appRoot);

    for (const el of candidates) {
      if (!el) continue;
      const val = parseBg(el);
      if (val !== null) return val;
    }

    if (document.body) {
      const color = getComputedStyle(document.body).color;
      const tm = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (tm) {
        const textLum = lum(+tm[1]!, +tm[2]!, +tm[3]!);
        if (textLum > 0.5) return 0.1;
        if (textLum < 0.15) return 0.9;
      }
    }
  } catch (_) { /* noop */ }

  return null;
}

/**
 * Reactively sync the widget theme with the host page.
 * Monitors page attributes/classes and body background luminance.
 * Skips syncing when the user has manually overridden the theme via the toggle.
 * Returns a cleanup function that disconnects the observer and clears the timer.
 */
function watchTheme(
  shell: HTMLElement,
  configTheme?: 'light' | 'dark' | 'auto',
  isOverridden?: () => boolean,
  onSync?: () => void,
): () => void {
  if (configTheme === 'light' || configTheme === 'dark') return () => {};

  const sync = () => {
    if (!isOverridden?.()) {
      const detected = detectTheme(configTheme);
      if (shell.getAttribute('data-theme') !== detected) {
        shell.setAttribute('data-theme', detected);
      }
    }
    onSync?.();
  };

  const observer = new MutationObserver(sync);
  const opts: MutationObserverInit = {
    attributes: true,
    attributeFilter: [
      'class', 'style',
      'data-theme', 'data-color-scheme', 'data-bs-theme',
      'data-color-mode', 'data-mode', 'data-appearance',
    ],
  };
  observer.observe(document.documentElement, opts);
  if (document.body) {
    observer.observe(document.body, opts);
    const appRoot = document.body.firstElementChild;
    if (appRoot && !appRoot.hasAttribute('data-aura')) {
      observer.observe(appRoot, opts);
    }
  }

  const intervalId = setInterval(sync, 2000);

  return () => {
    observer.disconnect();
    clearInterval(intervalId);
  };
}

/**
 * Initialize panel content with placeholder sections for feature modules
 */
function initializePanelContent(
  tabPanels: Map<string, HTMLElement>,
  _config: AuraConfig
): void {
  const profilesPanel = tabPanels.get('profiles');
  if (profilesPanel) {
    profilesPanel.innerHTML = `
      <div class="aura-panel__section">
        <h3 class="aura-panel__section-title">Choose the right accessibility profile for you</h3>
        <div class="aura-panel__profiles" id="aura-profiles-container">
        </div>
      </div>
    `;
  }

  const contentPanel = tabPanels.get('content');
  if (contentPanel) {
    contentPanel.innerHTML = `
      <div class="aura-panel__section">
        <h3 class="aura-panel__section-title">Content Adjustments</h3>
        <div id="aura-content-adjustments">
        </div>
      </div>
    `;
  }

  const colorsPanel = tabPanels.get('colors');
  if (colorsPanel) {
    colorsPanel.innerHTML = `
      <div class="aura-panel__section">
        <h3 class="aura-panel__section-title">Color Adjustments</h3>
        <div id="aura-color-adjustments">
        </div>
      </div>
    `;
  }

  const orientationPanel = tabPanels.get('orientation');
  if (orientationPanel) {
    orientationPanel.innerHTML = `
      <div class="aura-panel__section">
        <h3 class="aura-panel__section-title">Orientation Adjustments</h3>
        <div id="aura-orientation-adjustments">
        </div>
      </div>
    `;
  }
}
